/* eslint-disable spire/export-styles */
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import Block, { BlockProps, BlockVariant, BlockVariantSide } from "@store/Components/Common/Block";
import blockBackgroundImageField from "@store/Components/Common/Block/fields/backgroundImage";
import blockFullWidthField from "@store/Components/Common/Block/fields/fullWidth";
import blockPreserveSpacingField from "@store/Components/Common/Block/fields/preserveSpacing";
import blockRemoveMarginField from "@store/Components/Common/Block/fields/removeMargin";
import blockRemoveSidesMarginField from "@store/Components/Common/Block/fields/removeSidesMargin";
import blockVariantField from "@store/Components/Common/Block/fields/variant";
import blockVariantSideField from "@store/Components/Common/Block/fields/variantSide";
import gridVerticalGapField, { getVerticalGapData, VerticalGap } from "@store/fields/gridVerticalGap";
import { gridGapStyles } from "@store/theme/common/mixins";
/* Store Custom Code - End */

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // Store Custom Code
    blockPreserveSpacing = "blockPreserveSpacing",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    gridVerticalGap = "gridVerticalGap",
    blockBackgroundImage = "blockBackgroundImage",
    blockRemoveMargin = "blockRemoveMargin",
    blockRemoveSidesMargin = "blockRemoveSidesMargin",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // Store Custom Code
        [fields.blockPreserveSpacing]: boolean;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: BlockVariant;
        [fields.blockVariantSide]: BlockVariantSide;
        [fields.gridVerticalGap]: VerticalGap;
        [fields.blockBackgroundImage]: string;
        [fields.blockRemoveMargin]: boolean;
        [fields.blockRemoveSidesMargin]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    /* Store Custom Code - Start */
    1: [3, 2, 1, 1, 1],
    2: [4, 3, 2, 2, 2],
    3: [6, 3, 3, 3, 3],
    4: [6, 4, 4, 4, 4],
    5: [12, 5, 5, 5, 5],
    6: [12, 6, 6, 6, 6],
    7: [12, 7, 7, 7, 7],
    8: [12, 8, 8, 8, 8],
    9: [12, 9, 9, 9, 9],
    10: [12, 9, 10, 10, 10],
    11: [12, 10, 11, 11, 11],
    /* Store Custom Code - End */
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            isFullWidth,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    // Store Custom Code - Removed fullWidthBackgroundCss
    // Store Custom Code - Removed finalCss

    /* Store Custom Code - Start */
    const {
        fields: {
            blockPreserveSpacing,
            blockFullWidth,
            blockVariant,
            blockVariantSide,
            gridVerticalGap,
            blockBackgroundImage,
            blockRemoveMargin,
            blockRemoveSidesMargin,
        },
    } = props;

    const blockProps: BlockProps = {
        preserveSpacing: blockPreserveSpacing,
        fullWidth: blockFullWidth,
        variant: blockVariant,
        variantSide: blockVariantSide,
        backgroundImage: blockBackgroundImage,
        removeMargin: blockRemoveMargin,
        removeSidesMargin: blockRemoveSidesMargin,
    };

    const gridContainerProps = {
        gap: gridVerticalGap ? 0 : gap || 0,
        css: css`
            ${gridVerticalGap ? gridGapStyles([gap ? `${gap}px` : 0], getVerticalGapData(gridVerticalGap)) : null}
        `,
    };

    return (
        <Block {...blockProps}>
            <GridContainer {...gridContainerProps}>{columnElements}</GridContainer>
        </Block>
    );
    /* Store Custom Code - End */
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [12], // Store Custom Code
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 50, // Store Custom Code
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10, // Store Custom Code
            },
            // Store Custom Code - Removed isFullWidth
            // Store Custom Code - Removed fullWidthStylingType
            // Store Custom Code - Removed solidColor
            // Store Custom Code - Removed fullWidthColor
            // Store Custom Code - Removed fullWidthImage
            // Store Custom Code - Removed fullWidthImageFocalPoint
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            // Store Custom Code - Removed customCSS
            // Store Custom Code
            {
                ...blockVariantField,
                name: fields.blockVariant,
                sortOrder: 100,
                tab: advancedTab,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                sortOrder: 101,
                tab: advancedTab,
            },
            {
                ...gridVerticalGapField,
                name: fields.gridVerticalGap,
                tooltip: `${gridVerticalGapField.tooltip} (Default: Uses the \"Gap\" configuration)`,
                sortOrder: 102,
                tab: advancedTab,
            },
            {
                ...blockFullWidthField,
                name: fields.blockFullWidth,
                sortOrder: 103,
                tab: advancedTab,
            },
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
                sortOrder: 104,
                tab: advancedTab,
            },
            {
                ...blockBackgroundImageField,
                name: fields.blockBackgroundImage,
                sortOrder: 105,
                tab: advancedTab,
            },
            {
                ...blockRemoveMarginField,
                name: fields.blockRemoveMargin,
                sortOrder: 106,
                tab: advancedTab,
            },
            {
                ...blockRemoveSidesMarginField,
                name: fields.blockRemoveSidesMargin,
                sortOrder: 106,
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
